import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiChevronLeft,
    FiChevronRight,
    FiDownload,
    FiFileText,
    FiRefreshCw,
    FiSearch,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchPurchaseRegister,

    purchaseRegisterRecordsSelector,
    purchaseRegisterSummarySelector,
    purchaseRegisterPaginationSelector,

    purchaseReportLoadingSelector,
    purchaseReportErrorSelector,
} from "../../../../../../store/billing/purchaseReport/purchaseReportSlice";

import {
    exportPurchaseReport,
} from "./utils/purchaseReportExport";

import {
    fetchAllPurchaseReportRecords,
} from "./utils/fetchAllPurchaseReportRecords";

import ReportPeriodToolbar
    from "./components/ReportPeriodToolbar";

import SupplierSummaryReport
    from "./components/SupplierSummaryReport";

import DebitNoteReport
    from "./components/DebitNoteReport";

import PaymentOutReport
    from "./components/PaymentOutReport";

import OutstandingReport
    from "./components/OutstandingReport";

import AgeingReport
    from "./components/AgeingReport";

import GstInputReport
    from "./components/GstInputReport";

import "./PurchaseReports.css";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};


const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;


const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );


const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};


const getTodayInputValue =
    () => {
        const date =
            new Date();

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() + 1
            ).padStart(
                2,
                "0"
            );

        const day =
            String(
                date.getDate()
            ).padStart(
                2,
                "0"
            );

        return `${year}-${month}-${day}`;
    };


const getMonthStartInputValue =
    () => {
        const date =
            new Date();

        const year =
            date.getFullYear();

        const month =
            String(
                date.getMonth() + 1
            ).padStart(
                2,
                "0"
            );

        return `${year}-${month}-01`;
    };


const getSupplierName = (
    record
) =>
    record?.supplier?.supplierName ||
    record?.supplier?.companyName ||
    record?.supplier?.name ||
    "Supplier";


/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PurchaseReportsPage =
    () => {
        const dispatch =
            useDispatch();


        /*
        |--------------------------------------------------------------------------
        | EXPORT STATE
        |--------------------------------------------------------------------------
        */

        const [
            exporting,
            setExporting,
        ] = useState(false);


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const records =
            useSelector(
                purchaseRegisterRecordsSelector
            ) || [];

        const summary =
            useSelector(
                purchaseRegisterSummarySelector
            ) || {};

        const pagination =
            useSelector(
                purchaseRegisterPaginationSelector
            ) || {};

        const loading =
            useSelector(
                purchaseReportLoadingSelector
            ) || {};

        const errors =
            useSelector(
                purchaseReportErrorSelector
            ) || {};


        /*
        |--------------------------------------------------------------------------
        | ACTIVE TAB
        |--------------------------------------------------------------------------
        */

        const [
            activeTab,
            setActiveTab,
        ] = useState(
            "PURCHASE_REGISTER"
        );


        /*
        |--------------------------------------------------------------------------
        | COMMON REPORT PERIOD
        |--------------------------------------------------------------------------
        */

        const [
            fromDate,
            setFromDate,
        ] = useState(
            getMonthStartInputValue()
        );

        const [
            toDate,
            setToDate,
        ] = useState(
            getTodayInputValue()
        );

        const [
            financialYear,
            setFinancialYear,
        ] = useState("");


        /*
        |--------------------------------------------------------------------------
        | PURCHASE REGISTER FILTERS
        |--------------------------------------------------------------------------
        */

        const [
            status,
            setStatus,
        ] = useState("ALL");

        const [
            paymentStatus,
            setPaymentStatus,
        ] = useState("ALL");

        const [
            taxType,
            setTaxType,
        ] = useState("ALL");

        const [
            searchInput,
            setSearchInput,
        ] = useState("");

        const [
            search,
            setSearch,
        ] = useState("");

        const [
            page,
            setPage,
        ] = useState(1);

        const [
            limit,
            setLimit,
        ] = useState(20);

        const [
            sortBy,
            setSortBy,
        ] = useState(
            "documentDate"
        );

        const [
            sortOrder,
            setSortOrder,
        ] = useState(
            "desc"
        );


        /*
        |--------------------------------------------------------------------------
        | TABS
        |--------------------------------------------------------------------------
        */

        const tabs =
            useMemo(
                () => [
                    {
                        key:
                            "PURCHASE_REGISTER",
                        label:
                            "Purchase Register",
                    },

                    {
                        key:
                            "SUPPLIER_SUMMARY",
                        label:
                            "Supplier Summary",
                    },

                    {
                        key:
                            "DEBIT_NOTES",
                        label:
                            "Debit Notes",
                    },

                    {
                        key:
                            "PAYMENT_OUT",
                        label:
                            "Payment Out",
                    },

                    {
                        key:
                            "OUTSTANDING",
                        label:
                            "Outstanding",
                    },

                    {
                        key:
                            "AGEING",
                        label:
                            "Ageing",
                    },

                    {
                        key:
                            "GST_INPUT",
                        label:
                            "GST Input",
                    },
                ],
                []
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD PURCHASE REGISTER
        |--------------------------------------------------------------------------
        */

        const loadPurchaseRegister =
            useCallback(
                async () => {
                    if (
                        activeTab !==
                        "PURCHASE_REGISTER"
                    ) {
                        return;
                    }

                    await dispatch(
                        fetchPurchaseRegister({
                            page,
                            limit,

                            fromDate,
                            toDate,

                            status,
                            paymentStatus,
                            taxType,
                            financialYear,

                            search,

                            sortBy,
                            sortOrder,
                        })
                    );
                },
                [
                    dispatch,

                    activeTab,

                    page,
                    limit,

                    fromDate,
                    toDate,

                    status,
                    paymentStatus,
                    taxType,
                    financialYear,

                    search,

                    sortBy,
                    sortOrder,
                ]
            );


        useEffect(
            () => {
                loadPurchaseRegister();
            },
            [
                loadPurchaseRegister,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SEARCH DEBOUNCE
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                const timer =
                    setTimeout(
                        () => {
                            setSearch(
                                searchInput.trim()
                            );

                            setPage(1);
                        },
                        350
                    );

                return () =>
                    clearTimeout(timer);
            },
            [
                searchInput,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | TAB CHANGE
        |--------------------------------------------------------------------------
        */

        const handleTabChange =
            (
                tabKey
            ) => {
                setActiveTab(
                    tabKey
                );

                setPage(1);
            };


        /*
        |--------------------------------------------------------------------------
        | COMMON PERIOD HANDLERS
        |--------------------------------------------------------------------------
        */

        const handleFromDateChange =
            (
                value
            ) => {
                setFromDate(
                    value
                );

                setPage(1);
            };


        const handleToDateChange =
            (
                value
            ) => {
                setToDate(
                    value
                );

                setPage(1);
            };


        const handleFinancialYearChange =
            (
                value
            ) => {
                setFinancialYear(
                    value
                );

                setPage(1);
            };


        const handleResetPeriod =
            () => {
                setFromDate(
                    getMonthStartInputValue()
                );

                setToDate(
                    getTodayInputValue()
                );

                setFinancialYear(
                    ""
                );

                setPage(1);
            };


        /*
        |--------------------------------------------------------------------------
        | RESET PURCHASE REGISTER FILTERS
        |--------------------------------------------------------------------------
        |
        | Important:
        | This does NOT reset the common report period.
        |--------------------------------------------------------------------------
        */

        const handleResetRegisterFilters =
            () => {
                setSearchInput(
                    ""
                );

                setSearch(
                    ""
                );

                setStatus(
                    "ALL"
                );

                setPaymentStatus(
                    "ALL"
                );

                setTaxType(
                    "ALL"
                );

                setSortBy(
                    "documentDate"
                );

                setSortOrder(
                    "desc"
                );

                setPage(1);
            };


        /*
        |--------------------------------------------------------------------------
        | SUMMARY CARDS
        |--------------------------------------------------------------------------
        */

        const summaryCards =
            useMemo(
                () => [
                    {
                        label:
                            "Purchase Bills",

                        value:
                            toNumber(
                                summary?.totalBills
                            ),

                        help:
                            `${toNumber(
                                summary?.cancelledBills
                            )} cancelled`,
                    },

                    {
                        label:
                            "Grand Total",

                        value:
                            formatCurrency(
                                summary?.grandTotal
                            ),

                        help:
                            "Purchase value",
                    },

                    {
                        label:
                            "Input Tax",

                        value:
                            formatCurrency(
                                summary?.taxAmount
                            ),

                        help:
                            `CGST ${formatCurrency(
                                summary?.cgstAmount
                            )} • SGST ${formatCurrency(
                                summary?.sgstAmount
                            )}`,
                    },

                    {
                        label:
                            "Outstanding",

                        value:
                            formatCurrency(
                                summary?.outstandingAmount
                            ),

                        help:
                            `${toNumber(
                                summary?.unpaidBills
                            )} unpaid • ${toNumber(
                                summary?.partiallyPaidBills
                            )} partial`,
                    },
                ],
                [
                    summary,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | PAGINATION
        |--------------------------------------------------------------------------
        */

        const total =
            toNumber(
                pagination?.total
            );

        const currentPage =
            toNumber(
                pagination?.page,
                page
            ) || 1;

        const currentLimit =
            toNumber(
                pagination?.limit,
                limit
            ) || 20;

        const totalPages =
            Math.max(
                toNumber(
                    pagination?.totalPages,
                    1
                ),
                1
            );

        const fromRecord =
            total > 0
                ? (
                    currentPage - 1
                ) *
                    currentLimit +
                    1
                : 0;

        const toRecord =
            total > 0
                ? Math.min(
                    currentPage *
                        currentLimit,
                    total
                )
                : 0;


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const handleRefresh =
            () => {
                if (
                    activeTab ===
                    "PURCHASE_REGISTER"
                ) {
                    loadPurchaseRegister();
                }
            };


        /*
        |--------------------------------------------------------------------------
        | EXPORT ALL FILTERED RECORDS
        |--------------------------------------------------------------------------
        */

        const handleExport =
            async () => {
                try {
                    setExporting(
                        true
                    );

                    const commonFilters = {
                        fromDate,
                        toDate,
                        financialYear,
                    };

                    let filters = {
                        ...commonFilters,
                    };


                    switch (
                        activeTab
                    ) {
                        /*
                        |--------------------------------------------------------------------------
                        | PURCHASE REGISTER
                        |--------------------------------------------------------------------------
                        */

                        case "PURCHASE_REGISTER":
                            filters = {
                                ...commonFilters,

                                search,

                                status,
                                paymentStatus,
                                taxType,

                                sortBy,
                                sortOrder,
                            };

                            break;


                        /*
                        |--------------------------------------------------------------------------
                        | SUPPLIER SUMMARY
                        |--------------------------------------------------------------------------
                        */

                        case "SUPPLIER_SUMMARY":
                            filters = {
                                ...commonFilters,

                                status,
                                paymentStatus,
                                taxType,
                            };

                            break;


                        /*
                        |--------------------------------------------------------------------------
                        | DEBIT NOTES
                        |--------------------------------------------------------------------------
                        */

                        case "DEBIT_NOTES":
                            filters = {
                                fromDate,
                                toDate,
                            };

                            break;


                        /*
                        |--------------------------------------------------------------------------
                        | PAYMENT OUT
                        |--------------------------------------------------------------------------
                        */

                        case "PAYMENT_OUT":
                            filters = {
                                fromDate,
                                toDate,
                            };

                            break;


                        /*
                        |--------------------------------------------------------------------------
                        | OUTSTANDING
                        |--------------------------------------------------------------------------
                        */

                        case "OUTSTANDING":
                            filters = {
                                ...commonFilters,
                            };

                            break;


                        /*
                        |--------------------------------------------------------------------------
                        | AGEING
                        |--------------------------------------------------------------------------
                        */

                        case "AGEING":
                            filters = {
                                ...commonFilters,
                            };

                            break;


                        /*
                        |--------------------------------------------------------------------------
                        | GST INPUT
                        |--------------------------------------------------------------------------
                        */

                        case "GST_INPUT":
                            filters = {
                                ...commonFilters,

                                status:
                                    "CONFIRMED",
                            };

                            break;


                        default:
                            throw new Error(
                                "Invalid report selected."
                            );
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | FETCH ALL PAGES
                    |--------------------------------------------------------------------------
                    */

                    const exportRecords =
                        await fetchAllPurchaseReportRecords({
                            reportType:
                                activeTab,

                            filters,
                        });


                    /*
                    |--------------------------------------------------------------------------
                    | VALIDATE
                    |--------------------------------------------------------------------------
                    */

                    if (
                        !exportRecords.length
                    ) {
                        window.alert(
                            "No records available to export."
                        );

                        return;
                    }


                    /*
                    |--------------------------------------------------------------------------
                    | DOWNLOAD
                    |--------------------------------------------------------------------------
                    */

                    exportPurchaseReport({
                        reportType:
                            activeTab,

                        records:
                            exportRecords,
                    });

                } catch (
                    error
                ) {
                    console.error(
                        "[PurchaseReports] Export:",
                        error
                    );

                    window.alert(
                        error?.message ||
                        "Unable to export purchase report."
                    );

                } finally {
                    setExporting(
                        false
                    );
                }
            };


        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="purchase-report-page">

                {/* =====================================================
                    HEADER
                ===================================================== */}

                <header className="purchase-report-header">

                    <div>

                        <small>
                            PURCHASE
                        </small>

                        <h1>
                            Purchase Reports
                        </h1>

                        <p>
                            Analyse purchases, taxes, supplier payments,
                            debit notes and outstanding balances.
                        </p>

                    </div>


                    <div className="purchase-report-header-actions">

                        {activeTab ===
                            "PURCHASE_REGISTER" && (

                            <button
                                type="button"
                                className="purchase-report-button"
                                disabled={
                                    loading.register
                                }
                                onClick={
                                    handleRefresh
                                }
                            >
                                <FiRefreshCw
                                    className={
                                        loading.register
                                            ? "purchase-report-spinner"
                                            : ""
                                    }
                                />

                                Refresh
                            </button>

                        )}


                        <button
                            type="button"
                            className="purchase-report-button purchase-report-button-primary"
                            disabled={
                                exporting
                            }
                            onClick={
                                handleExport
                            }
                        >

                            {exporting ? (

                                <FiRefreshCw className="purchase-report-spinner" />

                            ) : (

                                <FiDownload />

                            )}

                            {exporting
                                ? "Exporting..."
                                : "Export"}

                        </button>

                    </div>

                </header>


                {/* =====================================================
                    TABS
                ===================================================== */}

                <div className="purchase-report-tabs">

                    {tabs.map(
                        (
                            tab
                        ) => (

                            <button
                                key={
                                    tab.key
                                }
                                type="button"
                                className={
                                    activeTab ===
                                    tab.key
                                        ? "active"
                                        : ""
                                }
                                onClick={() =>
                                    handleTabChange(
                                        tab.key
                                    )
                                }
                            >
                                {tab.label}
                            </button>

                        )
                    )}

                </div>


                {/* =====================================================
                    COMMON REPORT PERIOD

                    This is now available for ALL report tabs.
                ===================================================== */}

                <ReportPeriodToolbar
                    fromDate={
                        fromDate
                    }

                    toDate={
                        toDate
                    }

                    financialYear={
                        financialYear
                    }

                    onFromDateChange={
                        handleFromDateChange
                    }

                    onToDateChange={
                        handleToDateChange
                    }

                    onFinancialYearChange={
                        handleFinancialYearChange
                    }

                    onReset={
                        handleResetPeriod
                    }
                />


                {/* =====================================================
                    PURCHASE REGISTER
                ===================================================== */}

                {activeTab ===
                    "PURCHASE_REGISTER" && (

                    <>

                        {/* ERROR */}

                        {errors.register && (

                            <div className="purchase-report-alert">

                                <FiAlertCircle />

                                <span>
                                    {errors.register}
                                </span>

                            </div>

                        )}


                        {/* SUMMARY */}

                        <section className="purchase-report-summary-grid">

                            {summaryCards.map(
                                (
                                    card
                                ) => (

                                    <div
                                        key={
                                            card.label
                                        }
                                        className="purchase-report-summary-card"
                                    >

                                        <span>
                                            {card.label}
                                        </span>

                                        <strong>
                                            {card.value}
                                        </strong>

                                        <small>
                                            {card.help}
                                        </small>

                                    </div>

                                )
                            )}

                        </section>


                        {/* TAX SUMMARY */}

                        <section className="purchase-report-tax-summary">

                            <div>

                                <span>
                                    Taxable
                                </span>

                                <strong>
                                    {formatCurrency(
                                        summary?.taxableAmount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    CGST
                                </span>

                                <strong>
                                    {formatCurrency(
                                        summary?.cgstAmount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    SGST
                                </span>

                                <strong>
                                    {formatCurrency(
                                        summary?.sgstAmount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    IGST
                                </span>

                                <strong>
                                    {formatCurrency(
                                        summary?.igstAmount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    Debit Notes
                                </span>

                                <strong>
                                    {formatCurrency(
                                        summary?.debitNoteAmount
                                    )}
                                </strong>

                            </div>


                            <div>

                                <span>
                                    Paid
                                </span>

                                <strong>
                                    {formatCurrency(
                                        summary?.paidAmount
                                    )}
                                </strong>

                            </div>

                        </section>


                        {/* =================================================
                            PURCHASE REGISTER FILTERS

                            Date + FY removed because they are now
                            controlled by ReportPeriodToolbar.
                        ================================================= */}

                        <section className="purchase-report-toolbar">

                            <div className="purchase-report-search">

                                <FiSearch />

                                <input
                                    type="text"
                                    value={
                                        searchInput
                                    }
                                    placeholder="Search bill, supplier, invoice, GSTIN..."
                                    onChange={(
                                        event
                                    ) =>
                                        setSearchInput(
                                            event.target.value
                                        )
                                    }
                                />

                            </div>


                            <select
                                value={
                                    status
                                }
                                onChange={(
                                    event
                                ) => {
                                    setStatus(
                                        event.target.value
                                    );

                                    setPage(1);
                                }}
                            >

                                <option value="ALL">
                                    All Status
                                </option>

                                <option value="DRAFT">
                                    Draft
                                </option>

                                <option value="CONFIRMED">
                                    Confirmed
                                </option>

                                <option value="CANCELLED">
                                    Cancelled
                                </option>

                            </select>


                            <select
                                value={
                                    paymentStatus
                                }
                                onChange={(
                                    event
                                ) => {
                                    setPaymentStatus(
                                        event.target.value
                                    );

                                    setPage(1);
                                }}
                            >

                                <option value="ALL">
                                    All Payments
                                </option>

                                <option value="UNPAID">
                                    Unpaid
                                </option>

                                <option value="PARTIALLY_PAID">
                                    Partially Paid
                                </option>

                                <option value="PAID">
                                    Paid
                                </option>

                            </select>


                            <select
                                value={
                                    taxType
                                }
                                onChange={(
                                    event
                                ) => {
                                    setTaxType(
                                        event.target.value
                                    );

                                    setPage(1);
                                }}
                            >

                                <option value="ALL">
                                    All Tax Types
                                </option>

                                <option value="CGST_SGST">
                                    CGST + SGST
                                </option>

                                <option value="IGST">
                                    IGST
                                </option>

                            </select>


                            <button
                                type="button"
                                className="purchase-report-button"
                                onClick={
                                    handleResetRegisterFilters
                                }
                            >
                                Reset Filters
                            </button>

                        </section>


                        {/* =================================================
                            SORT
                        ================================================= */}

                        <section className="purchase-report-sortbar">

                            <div>

                                <FiFileText />

                                <span>
                                    Purchase Register
                                </span>

                            </div>


                            <div>

                                <select
                                    value={
                                        sortBy
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        setSortBy(
                                            event.target.value
                                        );

                                        setPage(1);
                                    }}
                                >

                                    <option value="documentDate">
                                        Bill Date
                                    </option>

                                    <option value="grandTotal">
                                        Grand Total
                                    </option>

                                    <option value="taxableAmount">
                                        Taxable
                                    </option>

                                    <option value="taxAmount">
                                        Tax
                                    </option>

                                    <option value="paidAmount">
                                        Paid
                                    </option>

                                    <option value="outstandingAmount">
                                        Outstanding
                                    </option>

                                </select>


                                <select
                                    value={
                                        sortOrder
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        setSortOrder(
                                            event.target.value
                                        );

                                        setPage(1);
                                    }}
                                >

                                    <option value="desc">
                                        Descending
                                    </option>

                                    <option value="asc">
                                        Ascending
                                    </option>

                                </select>

                            </div>

                        </section>


                        {/* =================================================
                            TABLE
                        ================================================= */}

                        <section className="purchase-report-table-card">

                            {loading.register &&
                            records.length === 0 ? (

                                <div className="purchase-report-empty">

                                    <FiRefreshCw className="purchase-report-spinner" />

                                    <strong>
                                        Loading purchase register...
                                    </strong>

                                </div>

                            ) : records.length === 0 ? (

                                <div className="purchase-report-empty">

                                    <FiFileText />

                                    <strong>
                                        No purchase records found
                                    </strong>

                                    <span>
                                        Try changing the date or report filters.
                                    </span>

                                </div>

                            ) : (

                                <div className="purchase-report-table-wrapper">

                                    <table className="purchase-report-table">

                                        <thead>

                                            <tr>

                                                <th>
                                                    Bill
                                                </th>

                                                <th>
                                                    Date
                                                </th>

                                                <th>
                                                    Supplier
                                                </th>

                                                <th>
                                                    Supplier Invoice
                                                </th>

                                                <th className="text-right">
                                                    Taxable
                                                </th>

                                                <th className="text-right">
                                                    CGST
                                                </th>

                                                <th className="text-right">
                                                    SGST
                                                </th>

                                                <th className="text-right">
                                                    IGST
                                                </th>

                                                <th className="text-right">
                                                    Total
                                                </th>

                                                <th className="text-right">
                                                    Debit Note
                                                </th>

                                                <th className="text-right">
                                                    Paid
                                                </th>

                                                <th className="text-right">
                                                    Outstanding
                                                </th>

                                                <th>
                                                    Status
                                                </th>

                                                <th>
                                                    Payment
                                                </th>

                                            </tr>

                                        </thead>


                                        <tbody>

                                            {records.map(
                                                (
                                                    record,
                                                    index
                                                ) => (

                                                    <tr
                                                        key={
                                                            record?._id ||
                                                            index
                                                        }
                                                    >

                                                        {/* BILL */}

                                                        <td>

                                                            <strong className="purchase-report-bill-number">

                                                                {record?.documentNumber ||
                                                                    record?.purchaseBillNumber ||
                                                                    "-"}

                                                            </strong>

                                                        </td>


                                                        {/* DATE */}

                                                        <td>

                                                            {formatDate(
                                                                record?.documentDate
                                                            )}

                                                        </td>


                                                        {/* SUPPLIER */}

                                                        <td>

                                                            <div className="purchase-report-supplier">

                                                                <strong>

                                                                    {getSupplierName(
                                                                        record
                                                                    )}

                                                                </strong>

                                                                <span>

                                                                    {record?.supplier?.gstin ||
                                                                        record?.supplier?.mobile ||
                                                                        "-"}

                                                                </span>

                                                            </div>

                                                        </td>


                                                        {/* SUPPLIER INVOICE */}

                                                        <td>

                                                            <div className="purchase-report-supplier">

                                                                <strong>

                                                                    {record?.supplierInvoiceNumber ||
                                                                        "-"}

                                                                </strong>

                                                                <span>

                                                                    {formatDate(
                                                                        record?.supplierInvoiceDate
                                                                    )}

                                                                </span>

                                                            </div>

                                                        </td>


                                                        {/* TAXABLE */}

                                                        <td className="text-right">

                                                            {formatCurrency(
                                                                record?.taxableAmount
                                                            )}

                                                        </td>


                                                        {/* CGST */}

                                                        <td className="text-right">

                                                            {formatCurrency(
                                                                record?.cgstAmount
                                                            )}

                                                        </td>


                                                        {/* SGST */}

                                                        <td className="text-right">

                                                            {formatCurrency(
                                                                record?.sgstAmount
                                                            )}

                                                        </td>


                                                        {/* IGST */}

                                                        <td className="text-right">

                                                            {formatCurrency(
                                                                record?.igstAmount
                                                            )}

                                                        </td>


                                                        {/* TOTAL */}

                                                        <td className="text-right">

                                                            <strong>

                                                                {formatCurrency(
                                                                    record?.grandTotal
                                                                )}

                                                            </strong>

                                                        </td>


                                                        {/* DEBIT NOTE */}

                                                        <td className="text-right">

                                                            {formatCurrency(
                                                                record?.debitNoteAmount
                                                            )}

                                                        </td>


                                                        {/* PAID */}

                                                        <td className="text-right">

                                                            {formatCurrency(
                                                                record?.paidAmount
                                                            )}

                                                        </td>


                                                        {/* OUTSTANDING */}

                                                        <td className="text-right">

                                                            <strong
                                                                className={
                                                                    toNumber(
                                                                        record?.outstandingAmount
                                                                    ) > 0
                                                                        ? "purchase-report-outstanding"
                                                                        : ""
                                                                }
                                                            >

                                                                {formatCurrency(
                                                                    record?.outstandingAmount
                                                                )}

                                                            </strong>

                                                        </td>


                                                        {/* STATUS */}

                                                        <td>

                                                            <span
                                                                className={`purchase-report-status purchase-report-status-${String(
                                                                    record?.documentStatus ||
                                                                    ""
                                                                ).toLowerCase()}`}
                                                            >

                                                                {record?.documentStatus ||
                                                                    "-"}

                                                            </span>

                                                        </td>


                                                        {/* PAYMENT STATUS */}

                                                        <td>

                                                            <span
                                                                className={`purchase-report-payment-status purchase-report-payment-${String(
                                                                    record?.paymentStatus ||
                                                                    ""
                                                                )
                                                                    .toLowerCase()
                                                                    .replace(
                                                                        /_/g,
                                                                        "-"
                                                                    )}`}
                                                            >

                                                                {String(
                                                                    record?.paymentStatus ||
                                                                    "-"
                                                                ).replace(
                                                                    /_/g,
                                                                    " "
                                                                )}

                                                            </span>

                                                        </td>

                                                    </tr>

                                                )
                                            )}

                                        </tbody>

                                    </table>

                                </div>

                            )}


                            {/* =================================================
                                PAGINATION
                            ================================================= */}

                            <div className="purchase-report-pagination">

                                <div>

                                    Showing{" "}

                                    <strong>
                                        {fromRecord}
                                    </strong>

                                    {" - "}

                                    <strong>
                                        {toRecord}
                                    </strong>

                                    {" of "}

                                    <strong>
                                        {total}
                                    </strong>

                                </div>


                                <div className="purchase-report-pagination-controls">

                                    <select
                                        value={
                                            limit
                                        }
                                        onChange={(
                                            event
                                        ) => {
                                            setLimit(
                                                Number(
                                                    event.target.value
                                                )
                                            );

                                            setPage(1);
                                        }}
                                    >

                                        <option value={10}>
                                            10 / page
                                        </option>

                                        <option value={20}>
                                            20 / page
                                        </option>

                                        <option value={50}>
                                            50 / page
                                        </option>

                                        <option value={100}>
                                            100 / page
                                        </option>

                                    </select>


                                    <button
                                        type="button"
                                        disabled={
                                            currentPage <=
                                            1
                                        }
                                        onClick={() =>
                                            setPage(
                                                (
                                                    current
                                                ) =>
                                                    Math.max(
                                                        current - 1,
                                                        1
                                                    )
                                            )
                                        }
                                    >
                                        <FiChevronLeft />
                                    </button>


                                    <span>

                                        Page{" "}

                                        <strong>
                                            {currentPage}
                                        </strong>

                                        {" of "}

                                        <strong>
                                            {totalPages}
                                        </strong>

                                    </span>


                                    <button
                                        type="button"
                                        disabled={
                                            currentPage >=
                                            totalPages
                                        }
                                        onClick={() =>
                                            setPage(
                                                (
                                                    current
                                                ) =>
                                                    Math.min(
                                                        current + 1,
                                                        totalPages
                                                    )
                                            )
                                        }
                                    >
                                        <FiChevronRight />
                                    </button>

                                </div>

                            </div>

                        </section>

                    </>

                )}


                {/* =====================================================
                    SUPPLIER SUMMARY
                ===================================================== */}

                {activeTab ===
                    "SUPPLIER_SUMMARY" && (

                    <SupplierSummaryReport
                        fromDate={
                            fromDate
                        }

                        toDate={
                            toDate
                        }

                        status={
                            status
                        }

                        paymentStatus={
                            paymentStatus
                        }

                        taxType={
                            taxType
                        }

                        financialYear={
                            financialYear
                        }
                    />

                )}


                {/* =====================================================
                    DEBIT NOTES
                ===================================================== */}

                {activeTab ===
                    "DEBIT_NOTES" && (

                    <DebitNoteReport
                        fromDate={
                            fromDate
                        }

                        toDate={
                            toDate
                        }
                    />

                )}


                {/* =====================================================
                    PAYMENT OUT
                ===================================================== */}

                {activeTab ===
                    "PAYMENT_OUT" && (

                    <PaymentOutReport
                        fromDate={
                            fromDate
                        }

                        toDate={
                            toDate
                        }
                    />

                )}


                {/* =====================================================
                    OUTSTANDING
                ===================================================== */}

                {activeTab ===
                    "OUTSTANDING" && (

                    <OutstandingReport
                        fromDate={
                            fromDate
                        }

                        toDate={
                            toDate
                        }

                        financialYear={
                            financialYear
                        }
                    />

                )}


                {/* =====================================================
                    AGEING
                ===================================================== */}

                {activeTab ===
                    "AGEING" && (

                    <AgeingReport
                        fromDate={
                            fromDate
                        }

                        toDate={
                            toDate
                        }

                        financialYear={
                            financialYear
                        }
                    />

                )}


                {/* =====================================================
                    GST INPUT
                ===================================================== */}

                {activeTab ===
                    "GST_INPUT" && (

                    <GstInputReport
                        fromDate={
                            fromDate
                        }

                        toDate={
                            toDate
                        }

                        financialYear={
                            financialYear
                        }
                    />

                )}

            </div>
        );
    };


export default PurchaseReportsPage;